import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type { ReservationAction } from "../../types/reservation";

export type ReservationConfirmationAction = ReservationAction | "restore" | "release";

const copy: Record<ReservationConfirmationAction, { title: string; description: string; confirm: string }> = {
  reserve: {
    title: "Забронировать подарок?",
    description: "Другие участники увидят, что подарок уже занят, но не узнают кем.",
    confirm: "Забронировать",
  },
  cancel: {
    title: "Отменить бронь?",
    description: "Подарок снова станет доступен другим участникам семьи.",
    confirm: "Отменить бронь",
  },
  purchase: {
    title: "Подарок уже куплен?",
    description: "Отметим подарок купленным. Получатель по-прежнему не увидит этот статус.",
    confirm: "Да, куплен",
  },
  restore: {
    title: "Вернуть подарок в брони?",
    description: "Статус изменится на «Забронировано», а подарок останется закреплён за вами.",
    confirm: "Вернуть в брони",
  },
  release: {
    title: "Освободить подарок?",
    description: "Бронь будет снята, и подарок снова станет доступен другим участникам семьи.",
    confirm: "Освободить подарок",
  },
};

interface Props {
  action: ReservationConfirmationAction;
  wishTitle: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}

export function ReservationActionConfirm({
  action,
  wishTitle,
  pending,
  error,
  onClose,
  onConfirm,
}: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const content = copy[action];

  useEffect(() => {
    const previousFocus = document.activeElement;
    confirmRef.current?.focus();
    return () => {
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, []);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pending) onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onClose, pending]);

  const trapFocus = (event: ReactKeyboardEvent) => {
    if (event.key !== "Tab") return;
    if (event.shiftKey && document.activeElement === cancelRef.current) {
      event.preventDefault();
      confirmRef.current?.focus();
    } else if (!event.shiftKey && document.activeElement === confirmRef.current) {
      event.preventDefault();
      cancelRef.current?.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-5 py-8 backdrop-blur-sm">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="reservation-action-title"
        aria-describedby="reservation-action-description"
        onKeyDown={trapFocus}
        className="w-full max-w-sm rounded-[28px] bg-white p-6 text-center shadow-2xl"
      >
        <h2 id="reservation-action-title" className="text-2xl font-extrabold tracking-[-0.04em] text-[#171714]">
          {content.title}
        </h2>
        <p className="mt-2 font-semibold text-[#171714]">{wishTitle}</p>
        <p id="reservation-action-description" className="mt-2 text-sm leading-6 text-[#77776F]">
          {content.description}
        </p>
        {error && (
          <div role="alert" className="mt-4 rounded-2xl bg-[#FEF2F2] px-4 py-3 text-sm font-semibold text-[#B91C1C]">
            {error}
          </div>
        )}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={pending}
            className="h-12 cursor-pointer rounded-xl bg-[#F2F1EC] text-sm font-bold text-[#171714] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714] disabled:cursor-not-allowed disabled:opacity-60"
          >
            Назад
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="h-12 cursor-pointer rounded-xl bg-[#FF7A00] px-3 text-sm font-bold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF7A00] disabled:cursor-wait disabled:bg-[#FDBA74]"
          >
            {pending ? "Сохраняем…" : content.confirm}
          </button>
        </div>
      </section>
    </div>
  );
}
